import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import moment from 'moment';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import ListNavigationTab from '../../../component/listNavigationTab/ListNavigationTab';
import BreadCrumbs from '../../../component/breadcrumbs/BreadCrumbs';
import Title from '../../../component/title/Title';
import Table from '../../../component/table/Table';
import { CreateWithIcon } from '../../../component/buttonComponent/ButtonComponent';
import VehicleTableEmptyState from '../../../component/tableEmptyState/VehicleTableEmptyState';
import SnackBar from '../../../component/snackbar/Snackbar';
import TruckIcon from '../../../image/truckIcon.svg';
import { useApiData } from '../../../config/reducer/Modules';
import { APIGetVehicleAdminPresets } from '../../../config/restAPI/FetchAPI';
import { APIDeleteVehicleAdminPresets } from '../../../config/restAPI/DeleteAPI';
import Modal from '../../../component/modal/Modal';
import './VehicleConfigurator.css';
import '../geofence/geofence.css';

const seperator = [
    <img loading="lazy" src={TruckIcon} alt="live" style={{ display: 'flex', alignItem: 'center' }} />,
    <div style={{ color: '#475467', fontSize: '14px', fontWeight: 500 }}>Vehicle Configurator</div>,
];

const extractVehiclePresetList = (result) => {
    const payload = result?.data;

    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    if (Array.isArray(payload?.vehicleAdminPresets)) {
        return payload.vehicleAdminPresets;
    }

    if (Array.isArray(payload?.presets)) {
        return payload.presets;
    }

    if (Array.isArray(payload?.rows)) {
        return payload.rows;
    }

    return [];
};

const extractVehiclePresetTotalRows = (result, fallbackLength = 0) => {
    const payload = result?.data;

    if (typeof payload?.totalData === 'number') return payload.totalData;
    if (typeof payload?.totalRows === 'number') return payload.totalRows;
    if (typeof payload?.total === 'number') return payload.total;
    if (typeof payload?.count === 'number') return payload.count;
    if (Array.isArray(payload?.data)) return payload.data.length;
    if (Array.isArray(payload?.vehicleAdminPresets)) return payload.vehicleAdminPresets.length;
    if (Array.isArray(payload)) return payload.length;

    return fallbackLength;
};

const VehicleConfigurator = () => {
    const { apiResponse } = useApiData();
    const navigate = useNavigate();
    const location = useLocation();
    const token = Cookies.get('jwtToken');
    const agencyId = Cookies.get('agencyUUID');
    const [navigtionClick, setNavigtionClick, , setNavigationState] = useOutletContext();
    const [vehiclePolicies, setVehiclePolicies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [triggerSnackbar, settriggerSnackbar] = useState(false);
    const [textTitleSnackbar, setTextTitleSnackbar] = useState(null);
    const [textSubSnackbar, setTextSubSnackbar] = useState(null);
    const [moduleSnackbar, setModuleSnackbar] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedPolicyToDelete, setSelectedPolicyToDelete] = useState(null);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);

    const inventoryNavigationList = [
        {
            text: 'Assets & Devices', value: 1, path: '/inventory/assetdevices', enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner' || apiResponse?.role === 'System Administrator') ? true : apiResponse?.module?.fleetManagement?.asset,
        },
        {
            text: 'Connectivity', value: 4, path: '/inventory/connectivity', enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner') ? true : apiResponse?.module?.fleetManagement?.connectivity,
        },
        {
            text: 'Battery Configurator', value: 5, path: '/inventory/batteryconfigurator', enabled: apiResponse?.role === 'System Administrator',
        },
        {
            text: 'Vehicle Configurator', value: 6, path: '/inventory/vehicleconfigurator', enabled: apiResponse?.role === 'System Administrator',
        },
        {
            text: 'Fuel Price Registry', value: 7, path: '/inventory/priceRegistry', enabled: true,
        },
        {
            text: 'Fuel Configurator', value: 8, path: '/inventory/fuelconfigurator', enabled: apiResponse?.role === 'System Administrator',
        },
    ];

    const handleOpenDeleteModal = (selectedPolicy) => {
        setSelectedPolicyToDelete(selectedPolicy);
        setDeleteModal(true);
    };

    async function loadPolicies(pageNumber = 1, totalPerPage = perPage) {
        try {
            const result = await APIGetVehicleAdminPresets(setLoading, token, agencyId, {
                pageNumber,
                totalPerPage,
            });
            const statusCode = result?.status || result?.data?.status;
            const presetList = extractVehiclePresetList(result);
            const resolvedTotalRows = extractVehiclePresetTotalRows(result, presetList.length);

            if (statusCode === 200 || Array.isArray(presetList)) {
                const mappedPolicies = presetList.map((item) => ({
                    ...item,
                    id: item.oid || item.id,
                    presetName: item.presetName || item.name || '-',
                    vehicleType: item.vehicleType || '-',
                    description: item.description || '',
                    createdAt: item.createdAt || item.dateCreated || item.updatedAt,
                    dateCreated: {
                        date: item.createdAt || item.dateCreated || item.updatedAt ? moment(item.createdAt || item.dateCreated || item.updatedAt).format('DD MMM YYYY') : '-',
                        time: item.createdAt || item.dateCreated || item.updatedAt ? moment(item.createdAt || item.dateCreated || item.updatedAt).format('hh:mm:ssa [GMT+8]') : '-',
                    },
                    onDelete: (selectedPolicy) => {
                        handleOpenDeleteModal(selectedPolicy);
                    },
                }));
                setVehiclePolicies(mappedPolicies);
                setTotalRows(resolvedTotalRows);
                setLoading(false);
            } else {
                setVehiclePolicies([]);
                setTotalRows(0);
                setLoading(false);
            }
        } catch (error) {
            setVehiclePolicies([]);
            setTotalRows(0);
            setLoading(false);
        }
    }

    const handlePageChange = (page) => {
        loadPolicies(page, perPage);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        setPerPage(newPerPage);
        await loadPolicies(page, newPerPage);
    };

    const handleDeletePolicyConfirm = async () => {
        if (!selectedPolicyToDelete?.id) {
            setDeleteModal(false);
            return;
        }

        const deleteResult = await APIDeleteVehicleAdminPresets(token, setLoading, agencyId, {
            oid: [selectedPolicyToDelete.id],
        });

        if (deleteResult?.data?.status === 200 || deleteResult?.status === 200) {
            setDeleteModal(false);
            setSelectedPolicyToDelete(null);
            loadPolicies(1, perPage);
            settriggerSnackbar(true);
            setTextTitleSnackbar('Success');
            setTextSubSnackbar('Vehicle Policy Deleted!');
            setModuleSnackbar('success');
        } else {
            setDeleteModal(false);
            setSelectedPolicyToDelete(null);
            setLoading(false);
        }
    };

    const handleAddVehicleConfigButton = () => {
        navigate('/inventory/vehicleconfigurator/addVehicleConfig', { replace: true });
    };

    useEffect(() => {
        if (location.state) {
            if (location.state.snackbarStatus) {
                settriggerSnackbar(true);
                setTextTitleSnackbar(location.state.snackbarTitle);
                setTextSubSnackbar(location.state.snackbarSub);
                setModuleSnackbar(location.state.snackbarStatus);
                navigate(location.pathname, { replace: true });
            }
        }
    }, [location.state]);

    useEffect(() => {
        if (token) {
            loadPolicies();
        }
        setNavigtionClick(6);
        setNavigationState('inventory');
    }, []);

    return (
        <div className="inventory-container">
            <ListNavigationTab list={inventoryNavigationList} navigtionClick={navigtionClick} setNavigtionClick={setNavigtionClick} />
            <div className="inventory-header">
                <BreadCrumbs customSeperator={seperator} />
                <Title title="Vehicle Configurator" subtitle="This module is for configuring vehicle policy parameters that can be applied to different asset types" titleSize="1.875rem" />
            </div>
            <div className="inventory-body">
                <div className="inventory-body-filter">
                    <div />
                    <div className="inventory-filter-button">
                        <CreateWithIcon label="Create Vehicle Configurator" onClickFunction={handleAddVehicleConfigButton} buttonWidth="15.75rem" buttonHeight="2.75rem" module="policy" />
                    </div>
                </div>
                <div className="inventory-body-text">
                    {vehiclePolicies.length > 0 ? (
                        <Table
                          tableData={vehiclePolicies}
                          pagination
                          module="vehicleConfigurator"
                          handlePageChange={handlePageChange}
                          handlePerRowsChange={handlePerRowsChange}
                          loading={loading}
                          totalRows={totalRows}
                          backgroundColor="#F9FAFB"
                        />
                    ) : (
                        <div className="inventory-body-empty">
                            <VehicleTableEmptyState />
                        </div>
                    )}
                </div>
            </div>
            {
                triggerSnackbar && (
                <div className="geofence-snackbar-container">
                    <SnackBar state={triggerSnackbar} setState={settriggerSnackbar} title={textTitleSnackbar} subtitile={textSubSnackbar} module={moduleSnackbar} action={moduleSnackbar} />
                </div>
                )
            }
            {
                deleteModal && (
                    <div className="modal-container-table-bg">
                        <Modal
                          setSwitchModalShow={setDeleteModal}
                          switchModalShow={deleteModal}
                          btnConfirm={handleDeletePolicyConfirm}
                          module="DeleteModal"
                          source="VehiclePolicy"
                          dataRow={selectedPolicyToDelete}
                        />
                    </div>
                )
            }
        </div>
    );
};

export default VehicleConfigurator;
